/**
 * "My people": one list of the people under a supervisor, on My team's metrics, in the
 * Supervisors sidebar and on My metrics when the person shown supervises others.
 *
 * It replaced two sections — the "Your people" tree and the flat "Your whole team, one
 * list" — after Shayna's 2026-10-02 review (#11, #12): "Have only one chart with your
 * ppl. call it my people and before we had un collapse the person to see the hierarchy
 * rolling up to them."
 *
 * The team switch decides what it lists, as it decides every figure on the page:
 * - My whole team: the direct reports, where anyone who supervises others is one row
 *   carrying their whole team's rollup. Expand it to see the people rolling up to
 *   them, and keep going down. "Open" on that row opens the supervisor themself.
 * - Just my direct reports: the direct reports as people, each with their own figures.
 *
 * Each person is ranked inside their own job title, never against each other.
 */
import { useMemo } from 'react'
import { useAppData } from '@/data/AppData'
import { useFilters } from '@/data/filters'
import { METRICS, availableMetrics, personSubLine, standing } from '@/data/selectors'
import type { MetricKey, Person } from '@/data/selectors'
import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { PopOut } from '@/components/PopOut'
import { LowVolumeTag, RankCell, TerminatedChip, fmt } from '@/components/Bits'
import { ScoreTip } from '@/components/ScoreTip'
import { formatPoints, scoreBand, SCORE_MAX } from '@/measures/scorecard'
import { buildTeams, type Team } from '@/measures/teams'
import { childrenBySupervisor, peopleRows as buildPeopleRows, toggleOpen, type PeopleRow } from '@/measures/teamPeople'
import { money2, int, monthSpan } from '@/format'
import { define, withNote } from '@/measures/definitions'

export type TeamDepth = 'tree' | 'direct'

/**
 * The Report cell's floor: its 260px column less the cell's padding. A column width is
 * only a hint to an auto-layout table, and the people tables are wider than the
 * sidebar, so without this the names took the squeeze — four to six lines each — and
 * the measures fitted. The measures scroll sideways instead (Yehuda, 2026-09-30).
 */
const REPORT_MIN = 'calc(236px * var(--col-scale, 1))'

/** A team row's figure in a person column: the same measure, rolled up. */
const TEAM_VALUE: Partial<Record<MetricKey, (t: Team) => number | null>> = {
  billedPerMonth: (t) => t.perEmployeePerMonth,
  collectedPct: (t) => t.collection,
  closedClaimPct: (t) => t.closedPct,
  zeroTouchShare: (t) => t.zeroTouch,
  openWorkedPct: (t) => t.openWorked,
  facilities: (t) => t.facilitiesPerReport,
}

/** The scored per-report columns, in scorecard order. Cost per claim is already first. */
const DETAIL_METRIC_KEYS: MetricKey[] = [
  'billedPerMonth',
  'collectedPct',
  'closedClaimPct',
  'zeroTouchShare',
  'openWorkedPct',
  'facilities',
]

const byScore = (a: Person, b: Person) =>
  (a.score.scored ? a.score.total : 99) - (b.score.scored ? b.score.total : 99)

export function MyPeople({
  supervisor,
  depth,
  openList,
  onOpenList,
  onOpenPerson,
  pool,
  counts,
  heading = 'h2',
}: {
  /** Whose people. */
  supervisor: string
  /** The page's team switch: the hierarchy, or the direct reports only. */
  depth: TeamDepth
  /** Supervisors expanded in the hierarchy. */
  openList: string[]
  onOpenList: (next: string[]) => void
  /** A person, or a supervisor's "Open", was clicked. */
  onOpenPerson: (name: string) => void
  /** Who can be listed. `Scope.teamPool` unless the page narrows that to one person. */
  pool?: Person[]
  /**
   * Whose own figures can be listed; see `buildTeams`. Someone it leaves out who leads
   * a team is a sub-team row rather than missing (review #106). Anyone else it leaves
   * out is not listed.
   */
  counts?: (p: Person) => boolean
  /** The section heading's level, to sit with the page's other headings. */
  heading?: 'h2' | 'h3'
}) {
  const { derived, scope, coverage, label } = useAppData()
  const { filters } = useFilters()
  /** The months every rank here covers, for its hover. */
  const period = monthSpan(derived.months)
  const people = pool ?? scope.teamPool
  const open = useMemo(() => new Set(openList), [openList])

  // Whole trees: a supervisor's row rolls up everyone under them.
  const treeTeams = useMemo(
    () => buildTeams(people, derived.byName, 'tree', counts),
    [people, derived.byName, counts],
  )
  const treeByName = useMemo(() => new Map(treeTeams.map((t) => [t.supervisor, t])), [treeTeams])
  const tree = treeByName.get(supervisor) ?? null

  const children = useMemo(
    () =>
      childrenBySupervisor(
        counts ? people.filter((p) => counts(p) || treeByName.has(p.employee)) : people,
        (n) => treeByName.has(n),
      ),
    [people, treeByName, counts],
  )

  /**
   * The hierarchy in drill-down order, or the direct reports, lowest score first. A
   * direct report left out of the figures who leads a team is that team's row, so
   * with management off a manager's supervisors are still there to open (review #106).
   */
  const rows = useMemo<PeopleRow[]>(() => {
    if (depth === 'tree') return buildPeopleRows(children, treeByName, open, supervisor)
    const direct = tree?.direct ?? []
    const teams = direct
      .filter((p) => counts && !counts(p) && treeByName.has(p.employee))
      .map((p) => treeByName.get(p.employee)!)
      .sort((a, b) => a.supervisor.localeCompare(b.supervisor))
      .map((t): PeopleRow => ({ kind: 'team', t, indent: 0, open: false, fixed: true }))
    const persons = direct
      .filter((p) => !counts || counts(p))
      .sort(byScore)
      .map((p): PeopleRow => ({ kind: 'person', p, indent: 0 }))
    return [...teams, ...persons]
  }, [depth, children, treeByName, open, supervisor, tree, counts])
  const hasTeams = rows.some((r) => r.kind === 'team' && !r.fixed)

  /*
   * No sort on these columns: the rows are a tree, and a header click that sorted
   * people out from under their supervisor's row would break it. Each level is
   * already ordered — supervisors by name, then people lowest score first.
   */
  const columns: Column<PeopleRow>[] = [
    {
      key: 'person',
      header: 'Person',
      width: 260,
      render: (r) =>
        r.kind === 'team' ? (
          <div style={{ paddingLeft: r.indent * 18, minWidth: REPORT_MIN }}>
            <div style={{ fontSize: 'var(--t-table)', fontWeight: 600 }}>
              <span
                aria-hidden="true"
                style={{ display: 'inline-block', width: 14, color: 'var(--ink-3)' }}
              >
                {r.fixed ? '' : r.open ? '▾' : '▸'}
              </span>
              {r.t.supervisor}
              {/* The row expands; this opens the supervisor themself. */}
              <button
                type="button"
                className="byemp"
                style={{ marginLeft: 8 }}
                aria-label={`Open ${r.t.supervisor}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenPerson(r.t.supervisor)
                }}
              >
                Open
              </button>
            </div>
            <div
              className="mono"
              style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', paddingLeft: 14 }}
            >
              {r.t.ownTitle ?? 'Supervisor'} · team of {int(r.t.members.length)},{' '}
              {int(r.t.direct.length)} direct
              {r.fixed ? ' · own figures left out (management)' : r.open ? ' · collapse' : ' · expand'}
            </div>
          </div>
        ) : (
          <div style={{ paddingLeft: r.indent * 18 + (hasTeams ? 14 : 0), minWidth: REPORT_MIN }}>
            {/* The Employees page's person cell: chips, then title · holders · shore · vendor (review #107). */}
            <div style={{ fontSize: 'var(--t-table)' }}>
              {r.p.employee}
              {r.p.terminated && <> <TerminatedChip date={r.p.terminationDate} /></>}
              {r.p.management && <> <span className="pts-chip neut">mgmt</span></>}
            </div>
            <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
              {personSubLine(r.p, (scope.cohorts.get(r.p.jobTitle) ?? []).length)}
            </div>
          </div>
        ),
    },
    {
      key: 'score',
      header: `Score / ${SCORE_MAX}`,
      help: define('score'),
      align: 'right',
      width: 104,
      render: (r) => {
        if (r.kind === 'team') return <span style={{ color: 'var(--ink-3)' }}>team</span>
        const p = r.p
        return (
          <ScoreTip score={p.score} who={`${p.department} · ${p.jobTitle}`}>
            {p.score.scored ? (
              <>
                <span className={`scbadge ${scoreBand(p.score.total)}`}>{formatPoints(p.score.total)}</span>
                <LowVolumeTag lowVolume={p.score.lowVolume} />
              </>
            ) : (
              <span style={{ color: 'var(--ink-3)' }}>—</span>
            )}
          </ScoreTip>
        )
      },
    },
    {
      key: 'cpc',
      header: 'Cost / claim',
      help: withNote(define('costPerClaim'), 'A supervisor’s row shows their whole team’s rate.'),
      align: 'right',
      band: true,
      width: 150,
      render: (r) =>
        r.kind === 'team' ? (
          <span className="mono" style={{ fontSize: 'var(--t-table)', fontWeight: 600 }}>
            {money2(r.t.costPerClaim)}
          </span>
        ) : (
          <RankCell
            standing={standing(scope.cohorts, r.p, METRICS[0]!)}
            format="money2"
            label={METRICS[0]!.label}
            period={period}
          />
        ),
    },
    /*
     * The scored measures, one column each, ranked in the PERSON'S own job title.
     * Built from METRICS so a column here cannot drift from what the scorecard means
     * by the same word, and dropped when the bundle has no grain for it.
     */
    ...availableMetrics(coverage)
      .filter((m) => DETAIL_METRIC_KEYS.includes(m.key))
      .map(
        (m): Column<PeopleRow> => ({
          key: m.key,
          header: label(m.short),
          help: withNote(define(m.key), 'Ranked within the person’s own job title.'),
          align: 'right' as const,
          band: true,
          width: 116,
          // A supervisor's row carries the rolled-up figure, unranked: teams rank
          // against teams, not against the people in this list.
          render: (r: PeopleRow) =>
            r.kind === 'team' ? (
              <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
                {fmt(m.format, TEAM_VALUE[m.key]?.(r.t) ?? null)}
              </span>
            ) : (
              <RankCell standing={standing(scope.cohorts, r.p, m)} format={m.format} label={label(m.label)} period={period} />
            ),
        }),
      ),
  ]

  const H = heading
  return (
    <PopOut title="My people" note={supervisor}>
      {({ button, enlarged }) => (
        <section className="section popout-fill">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            {!enlarged && <H className="section-heading">My people</H>}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
              {hasTeams && (
                <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => onOpenList(treeTeams.map((t) => t.supervisor).sort())}
                  >
                    Expand all
                  </button>
                  <button type="button" className="btn" onClick={() => onOpenList([])}>
                    Collapse all
                  </button>
                </>
              )}
              {button}
            </span>
          </div>
          <p className="body-note" style={{ margin: '6px 0 12px' }}>
            {depth === 'tree'
              ? 'Your direct reports. Anyone who supervises others shows as one line with their whole team rolled up — expand it to see the people rolling up to them, and keep going down. Click a person to open them.'
              : rows.some((r) => r.kind === 'team')
                ? 'Your direct reports only. A supervisor or manager among them shows as their team, because management’s own figures are left out. Click a row to open them.'
                : 'Your direct reports only, each with their own figures. Switch to My whole team to see everyone rolling up to them. Click a person to open them.'}{' '}
            Scores and figures are for <b>{period}</b>.
          </p>
          <ExportBar
            filename="my-people.csv"
            rows={rows}
            columns={[
              { header: 'Row', value: (r) => (r.kind === 'team' ? 'Team' : 'Person') },
              { header: 'Name', value: (r) => (r.kind === 'team' ? r.t.supervisor : r.p.employee) },
              { header: 'Level', value: (r) => r.indent },
              { header: 'Job title', value: (r) => (r.kind === 'team' ? r.t.ownTitle : r.p.jobTitle) },
              { header: 'Team size', value: (r) => (r.kind === 'team' ? r.t.members.length : null) },
              {
                header: `Score / ${SCORE_MAX}, ${period}`,
                value: (r) => (r.kind === 'person' && r.p.score.scored ? r.p.score.total : null),
              },
              { header: 'Cost / claim', value: (r) => (r.kind === 'team' ? r.t.costPerClaim : r.p.costPerClaim) },
              ...availableMetrics(coverage)
                .filter((m) => DETAIL_METRIC_KEYS.includes(m.key))
                .map((m) => ({
                  header: m.short,
                  value: (r: PeopleRow) =>
                    r.kind === 'team' ? (TEAM_VALUE[m.key]?.(r.t) ?? null) : m.get(r.p),
                })),
            ]}
          />
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              storageKey="my-people"
              // No sort: a tree. Sorting would pull people out from under their supervisor; each level is already ordered.
              rows={rows}
              columns={columns}
              rowKey={(r) => (r.kind === 'team' ? `t:${r.t.supervisor}` : `p:${r.p.employee}`)}
              onRowClick={(r) =>
                r.kind === 'person'
                  ? onOpenPerson(r.p.employee)
                  : r.fixed
                    ? onOpenPerson(r.t.supervisor)
                    : onOpenList(toggleOpen(openList, r.t.supervisor))
              }
              empty={
                // Direct reports who supervise others are management, so they drop out
                // here with the switch off while still rolling up in My whole team.
                depth === 'direct' && filters.management === 'no' && (children.subs.get(supervisor)?.size ?? 0) > 0
                  ? 'Your direct reports all supervise others, and management is left out. Turn on Include management, or switch to My whole team.'
                  : 'Nobody reports to this supervisor under the current filters.'
              }
            />
          </div>
        </section>
      )}
    </PopOut>
  )
}
